// Move pieces between holding pallets (jobq-api, 2026-10-29). Mike, SO-1145: the Brake operator re-stacks
// formed parts the way they fit best after braking (PLT-Q8BA's 12 MKA onto PLT-J0C4, Q8BA left empty). An
// operator combines two of an order's holding pallets, or splits one onto a new pallet, with their PIN.
//
//   GET  /packaging/pallets/{id}/move-options  -> {pallet, movable, why, others [+ onto / from verdicts],
//                                                  new_pallet}; each verdict {ok, why, warnings}
//   POST /packaging/pallets/{id}/move-pieces   {pin, to_pallet_id | new_pallet, items?, override_weight_warning,
//                                                task_id?, client_request_id}
//
// The API decides every rule (same order, same step, laser done, limits); this file only words the answers.
// Pure / never throws, so the dialog and the tests share it.
import { beaconFetch } from '../../utils/beacons';

/** The order's holding pallets as the Move pieces dialog offers them. Never throws. */
export function loadMoveOptions(palletId) {
  return beaconFetch(`/packaging/pallets/${encodeURIComponent(palletId)}/move-options`);
}

/** POST move-pieces -> {ok, status, body, message, code, warnings}. `code` = 'over_limit' when the destination
 *  goes over the plan's limits (resend with override_weight_warning after the operator says so). */
export async function movePieces(palletId, body) {
  const r = await beaconFetch(`/packaging/pallets/${encodeURIComponent(palletId)}/move-pieces`, {
    method: 'POST', body: JSON.stringify(body),
  });
  const d = r.body && typeof r.body === 'object' && r.body.detail && typeof r.body.detail === 'object'
    ? r.body.detail : null;
  return { ...r, code: (d && d.code) || null, warnings: d && Array.isArray(d.warnings) ? d.warnings : [] };
}

/** "167 MKA" / "12 MKA, 4 MKB": the pieces on a pallet (or chosen), largest first. */
export function piecesText(parts) {
  return (parts || [])
    .filter((p) => p && Number(p.qty) > 0)
    .map((p) => `${p.qty} ${p.product_id || `part ${p.part_id}`}`)
    .join(', ');
}

/** "waiting for Brake" / "ready to pack": where a pallet's pieces are (its label's station). */
export function stationText(station) {
  const s = String(station || '').trim();
  if (!s || s.toLowerCase() === 'packing') return 'ready to pack';
  return `waiting for ${s}`;
}

/** The pieces on the pallet that can move (raw, cut): [{part_id, product_id, qty}]. */
export function movableParts(pallet) {
  return ((pallet && pallet.parts) || []).filter((p) => p && Number(p.qty) > 0 && !p.assemblies);
}

/** The items body for chosen quantities ({part_id: n}); null = everything on the pallet. Quantities left blank or
 *  equal to all of every part read as everything (the API then empties the pallet). */
export function itemsFor(pallet, chosen) {
  const parts = movableParts(pallet);
  const items = [];
  let all = true;
  for (const p of parts) {
    const raw = chosen ? chosen[p.part_id] : undefined;
    const n = raw === undefined || raw === '' ? Number(p.qty) : Math.floor(Number(raw));
    if (!Number.isFinite(n) || n < 0) return { error: `Type how many ${p.product_id || 'pieces'} to move.` };
    if (n !== Number(p.qty)) all = false;
    if (n > Number(p.qty)) return { error: `${pallet.short_id} holds ${p.qty} ${p.product_id}: move ${p.qty} or fewer.` };
    if (n > 0) items.push({ part_id: p.part_id, qty: n });
  }
  if (!items.length) return { error: 'Pick at least one piece to move.' };
  return { items: all ? null : items, count: items.reduce((s, i) => s + i.qty, 0) };
}

/** What happened, for the operator: where the pieces went, the emptied pallet, the new pallet's beacon. */
export function movedSentence(body) {
  if (!body || typeof body !== 'object') return 'Moved.';
  const from = body.from || {};
  const to = body.to || {};
  const what = piecesText(Object.values((body.moved || []).reduce((acc, m) => {
    const k = m.part_id;
    acc[k] = acc[k] || { part_id: k, product_id: m.product_id, qty: 0 };
    acc[k].qty += Number(m.qty) || 0;
    return acc;
  }, {}))) || `${body.qty || 0} pieces`;
  const out = [`Moved ${what} from ${from.short_id} onto ${to.created ? 'a new pallet, ' : ''}${to.short_id}.`];
  if (from.ended) {
    const b = (from.beacons_released || [])[0];
    out.push(`${from.short_id} is empty${b ? `: put its beacon ${b} back in the bin` : ''}.`);
  }
  if (to.created) {
    const b = to.beacon || {};
    if (b.beacon_id) out.push(`Clip beacon ${b.beacon_id} on ${to.short_id} (it is flashing in the bin).`);
    else out.push(`No free beacon for ${to.short_id}: print its status label.${b.free_note ? ` ${b.free_note}` : ''}`);
  }
  if (body.duplicate) out.push('(Already recorded: nothing more moved.)');
  return out.join(' ');
}
